import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, FileText, Lock, LogOut
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authLoading, setAuthLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "", excerpt: "", content: "", category: "", tags: "",
    featured_image: "", read_time: "5 min read", published: true,
    meta_title: "", meta_description: "", meta_keywords: "",
  });

  // Get auth header
  const getAuthHeader = () => {
    const stored = localStorage.getItem("admin_credentials");
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return { Authorization: `Basic ${btoa(`${username}:${password}`)}` };
    }
    return {};
  };

  // Check if already authenticated
  useEffect(() => {
    const stored = localStorage.getItem("admin_credentials");
    if (stored) {
      verifyAuth(JSON.parse(stored));
    } else {
      setLoading(false);
    }
  }, []);

  const verifyAuth = async (creds) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Basic ${btoa(`${creds.username}:${creds.password}`)}` }
      });
      setIsAuthenticated(true);
      localStorage.setItem("admin_credentials", JSON.stringify(creds));
      fetchBlogs();
    } catch {
      localStorage.removeItem("admin_credentials");
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await verifyAuth(credentials);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_credentials");
    setIsAuthenticated(false);
    setBlogs([]);
    toast.success("Logged out");
  };

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?published_only=false`);
      setBlogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  // Seed database on first load
  useEffect(() => {
    if (isAuthenticated) {
      axios.post(`${API}/seed`).catch(() => {});
    }
  }, [isAuthenticated]);

  const resetForm = () => {
    setFormData({
      title: "", excerpt: "", content: "", category: "", tags: "",
      featured_image: "", read_time: "5 min read", published: true,
      meta_title: "", meta_description: "", meta_keywords: "",
    });
    setEditingBlog(null);
    setShowForm(false);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title, excerpt: blog.excerpt, content: blog.content,
      category: blog.category, tags: blog.tags?.join(", ") || "",
      featured_image: blog.featured_image || "", read_time: blog.read_time,
      published: blog.published, meta_title: blog.meta_title || "",
      meta_description: blog.meta_description || "",
      meta_keywords: blog.meta_keywords?.join(", ") || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
      meta_keywords: formData.meta_keywords.split(",").map((k) => k.trim()).filter(Boolean),
    };

    try {
      if (editingBlog) {
        await axios.put(`${API}/admin/blogs/${editingBlog.slug}`, payload, { headers: getAuthHeader() });
        toast.success("Blog updated!");
      } else {
        await axios.post(`${API}/admin/blogs`, payload, { headers: getAuthHeader() });
        toast.success("Blog created!");
      }
      fetchBlogs();
      resetForm();
    } catch {
      toast.error("Failed to save blog");
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/admin/blogs/${slug}`, { headers: getAuthHeader() });
      toast.success("Blog deleted!");
      fetchBlogs();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const togglePublished = async (blog) => {
    try {
      await axios.put(`${API}/admin/blogs/${blog.slug}`, { published: !blog.published }, { headers: getAuthHeader() });
      toast.success(blog.published ? "Unpublished" : "Published");
      fetchBlogs();
    } catch {
      toast.error("Failed to update");
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // Login Form
  if (!isAuthenticated) {
    return (
      <>
        <SEO title="Admin Login | Saloni Shah" url="/admin" />
        <div className="min-h-screen pt-24 pb-20 bg-neutral-50 flex items-center justify-center">
          <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-serif font-bold text-2xl text-neutral-900">Admin Login</h1>
              <p className="text-neutral-600 text-sm mt-2">Enter your credentials to manage blog posts</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Username</label>
                <input
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  data-testid="login-username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  data-testid="login-password"
                />
              </div>
              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-primary text-white hover:bg-primary-light rounded-lg py-3 font-medium transition-all disabled:opacity-50"
                data-testid="login-submit"
              >
                {authLoading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  // Admin Dashboard
  return (
    <>
      <SEO title="Blog Management | Saloni Shah" url="/admin" />
      <div className="min-h-screen pt-24 pb-20 bg-neutral-50" data-testid="admin-page">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-serif font-bold text-2xl text-neutral-900">Blog Management</h1>
              <p className="text-neutral-600 text-sm">Create and manage your blog posts</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="bg-primary text-white hover:bg-primary-light rounded-lg px-4 py-2 text-sm font-medium transition-all flex items-center gap-2"
                data-testid="create-blog-btn"
              >
                <Plus className="w-4 h-4" /> New Post
              </button>
              <button
                onClick={handleLogout}
                className="bg-neutral-200 text-neutral-700 hover:bg-neutral-300 rounded-lg px-4 py-2 text-sm font-medium transition-all flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                  <h2 className="font-serif font-semibold text-lg">{editingBlog ? "Edit Post" : "New Post"}</h2>
                  <button onClick={resetForm} className="p-2 hover:bg-neutral-100 rounded-lg" data-testid="close-form-btn">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="blog-form">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" data-testid="form-title" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Category *</label>
                      <input type="text" required value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" data-testid="form-category" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Read Time</label>
                      <input type="text" value={formData.read_time} onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Excerpt *</label>
                    <textarea rows={2} required value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none resize-none" data-testid="form-excerpt" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Content * (Markdown)</label>
                    <textarea rows={12} required value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none resize-y font-mono text-sm" data-testid="form-content" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Featured Image URL</label>
                    <input type="url" value={formData.featured_image} onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                    <input type="text" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary outline-none" />
                  </div>

                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="published" checked={formData.published}
                      onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                      className="w-4 h-4 text-primary rounded focus:ring-primary" />
                    <label htmlFor="published" className="text-sm">Publish immediately</label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button type="submit" className="flex-1 bg-primary text-white rounded-lg py-2.5 font-medium hover:bg-primary-light flex items-center justify-center gap-2" data-testid="form-submit">
                      <Save className="w-4 h-4" /> {editingBlog ? "Update" : "Create"}
                    </button>
                    <button type="button" onClick={resetForm} className="px-6 py-2.5 border rounded-lg hover:bg-neutral-50">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Blog List */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-lg p-6 animate-pulse"><div className="h-5 bg-neutral-200 rounded w-3/4 mb-2" /><div className="h-4 bg-neutral-200 rounded w-1/4" /></div>)}
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl">
              <FileText className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-600">No blog posts yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blogs.map((blog) => (
                <div key={blog.id} className="bg-white rounded-lg p-5 border border-neutral-100 flex items-center justify-between" data-testid={`admin-blog-${blog.slug}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-neutral-900 truncate">{blog.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${blog.published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {blog.published ? "Live" : "Draft"}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500">{blog.category} • {formatDate(blog.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => togglePublished(blog)} className={`p-2 rounded-lg ${blog.published ? "hover:bg-yellow-50 text-yellow-600" : "hover:bg-green-50 text-green-600"}`} title={blog.published ? "Unpublish" : "Publish"}>
                      {blog.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleEdit(blog)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg" title="Edit">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(blog.slug)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
